/**
 * Formats a number as currency using German locale
 * @param amount - The amount to format
 * @param currency - The currency code (default: 'EUR')
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number, currency = 'EUR'): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

/**
 * Formats a date string using German locale
 * @param dateString - ISO date string to format
 * @returns Formatted date string (DD.MM.YYYY)
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Formats a date string with time using German locale
 * @param dateString - ISO date string to format
 * @returns Formatted date and time string (DD.MM.YYYY, HH:MM)
 */
export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const dateStr = date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timeStr = date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${dateStr}, ${timeStr}`;
};

/**
 * Gets the display label for a receipt status
 * @param status - The receipt status
 * @param canImport - Whether the receipt can be imported
 * @returns Human-readable status label
 */
export const getStatusLabel = (status: string, canImport: boolean): string => {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'text-extracting':
      return 'Extracting Text';
    case 'ready':
      return 'Ready';
    case 'generated':
      return canImport ? 'Ready to Import' : 'Generated';
    case 'modified':
      return 'Modified';
    case 'imported':
      return 'Imported';
    case 'text-extraction-failed':
      return 'Extraction Failed';
    case 'generation-scheduled':
      return 'Generation Scheduled';
    case 'generating':
      return 'Generating';
    case 'generation-failed':
      return 'Generation Failed';
    case 'import-scheduled':
      return 'Import Scheduled';
    case 'importing':
      return 'Importing';
    case 'import-failed':
      return 'Import Failed';
    default:
      return status;
  }
};

/**
 * Gets the display label for a booking type
 * @param type - The booking type
 * @returns Human-readable type label
 */
export const getBookingTypeLabel = (type: string): string => {
  return type === 'purchaseinvoice'
    ? 'Purchase Invoice'
    : 'Purchase Credit Note';
};
